import apiProxy from '@/app/features/shared/utils/apiProxy';
import { AppDispatch } from '@/app/store';
import {
  startLoadingLanguage,
  completeLoadingLanguage,
  failLoadingLanguage,
  LanguageCode,
} from '@/app/store/slices/languageSlice';
import * as coreConstants from '@/app/constants/coreConstants';
import * as apiConstants from '@/app/constants/apiConstants';
import { logger } from '@/app/utils/logger';

/**
 * Fetch user's language preference from the backend
 * @param userId - The user ID to fetch language preference for
 */
export const fetchUserLanguagePreference = (userId: number) => async (dispatch: AppDispatch) => {
  dispatch(startLoadingLanguage());
  
  try {
    const params = new URLSearchParams();
    params.append('userId', userId.toString());
    const queryString = params.toString();
    const url = `${apiConstants.API_BASE_URL}${apiConstants.API_PREFERENCES_LANGUAGE_GET}?${queryString}`;
    
    const response = await apiProxy({
      method: 'GET',
      url: url,
    });
    
    const result = response.data;
    
    if (result.succeeded && result.data && result.data.preference) {
      const languageCode = result.data.preference.languageCode as LanguageCode;
      dispatch(completeLoadingLanguage(languageCode));
    } else {
      // If no preference found, default to 'en-US'
      dispatch(completeLoadingLanguage('en-US'));
    }
  } catch (error: any) {
    logger.error('Failed to fetch user language preference', error);
    const errorMessage = error?.response?.data?.messages?.join(', ') || 'Failed to fetch language preference';
    dispatch(failLoadingLanguage(errorMessage));
    
    // Fallback to default language on error
    dispatch(completeLoadingLanguage('en-US'));
  }
};

/**
 * Update user's language preference in the backend with retry logic
 * @param userId - The user ID
 * @param languageCode - The language code to set
 * @param partitionKey - The partition key (defaults to SmartMatrix)
 * @param retryCount - Number of retries (defaults to 2)
 */
export const updateUserLanguagePreference = (
  userId: number,
  languageCode: LanguageCode,
  partitionKey: string = coreConstants.PARTITION_KEY,
  retryCount: number = 2
) => async (dispatch: AppDispatch) => {
  dispatch(startLoadingLanguage());
  
  const attemptUpdate = async (attemptsLeft: number): Promise<void> => {
    try {
      const url = `${apiConstants.API_BASE_URL}${apiConstants.API_PREFERENCES_LANGUAGE_UPDATE}`;
      
      const requestBody = {
        partitionKey: partitionKey,
        userId: userId,
        languageCode: languageCode,
      };
      
      logger.debug('updateUserLanguagePreference: Sending request', {
        url,
        requestBody,
        attemptsLeft
      });
      
      const response = await apiProxy({
        method: 'POST',
        url: url,
        data: requestBody,
      });
      
      logger.debug('updateUserLanguagePreference: Response received', { response });
      
      const result = response.data;
      
      if (result.succeeded && result.data && result.data.preference) {
        const updatedLanguageCode = result.data.preference.languageCode as LanguageCode;
        logger.info('updateUserLanguagePreference: Success! Updated to', { updatedLanguageCode });
        dispatch(completeLoadingLanguage(updatedLanguageCode));
      } else {
        const errorMessage = result.messages?.join(', ') || 'Failed to update language preference';
        logger.error('updateUserLanguagePreference: Failed with message', { errorMessage });
        
        // Retry if attempts left
        if (attemptsLeft > 0) {
          logger.info(`Retrying language update... (${attemptsLeft} attempts left)`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
          return attemptUpdate(attemptsLeft - 1);
        }
        
        dispatch(failLoadingLanguage(errorMessage));
      }
    } catch (error: any) {
      logger.error('updateUserLanguagePreference: Exception occurred', error);
      
      // Retry if attempts left
      if (attemptsLeft > 0) {
        logger.info(`Retrying language update after error... (${attemptsLeft} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        return attemptUpdate(attemptsLeft - 1);
      }
      
      const errorMessage = error?.response?.data?.messages?.join(', ') || 'Failed to update language preference';
      dispatch(failLoadingLanguage(errorMessage));
    }
  };
  
  await attemptUpdate(retryCount);
};
